import { useNavigate, useLocation, Link } from "react-router-dom";
import logo from "@/assets/tony-knows-email-logo.webp";
import { Button } from "@/components/ui/button";
import { useAssessmentState } from "@/hooks/useAssessmentState";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, RotateCcw, LayoutDashboard, LogOut, Menu, X, User, Compass, LogIn, UserCircle, UserPlus, ArrowLeft, Pencil, Eye, BarChart3, Compass as CompassIcon, TrendingUp, List, CreditCard, Settings, Trash2, ChevronRight, Heart, ClipboardList } from "lucide-react";
import { useShortlist } from "@/hooks/useShortlist";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import SignInModal from "@/components/auth/SignInModal";
import RetakeAssessmentDialog from "@/components/RetakeAssessmentDialog";
import { useUnlocks } from "@/hooks/useUnlocks";
import { useSubscription } from "@/hooks/useSubscription";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger, PopoverAnchor } from "@/components/ui/popover";
import { categoryLabel } from "@/lib/categoryLabels";
import { ENTRY_BARRIER_LABELS, type EntryBarrier } from "@/lib/careerFacets";
import type { Category } from "@/data/careers";

const MEGA_CATEGORIES: Category[] = [
  "AI, Data and Automation",
  "Aviation and Aerospace",
  "Beauty and Aesthetics",
  "Business, Finance and Legal",
  "Design and Creative",
  "Education, Science and Research",
  "Engineering, Construction and Infrastructure",
  "Green Energy and Sustainability",
  "Healthcare and Care",
  "Hospitality and Food",
  "Logistics and Supply Chain",
  "Maritime and Offshore",
  "Marketing and Media",
  "Mental Health and Therapy",
  "Public Sector and Emergency Services",
  "Sales, Retail and Customer Service",
  "Self-Employed and Business Owner",
  "Skilled Trades",
  "Sport, Fitness and Wellbeing",
  "Technology",
  "Transport and Logistics",
];
const MEGA_BARRIERS: EntryBarrier[] = [
  "no-degree",
  "entry-level",
  "fast-start",
  "apprenticeship",
  "requires-degree",
  "advanced-qual",
];
const MEGA_WORKSTYLES: { key: string; label: string; href: string }[] = [
  { key: "remote", label: "Remote", href: "/careers?work=remote" },
  { key: "hybrid", label: "Hybrid", href: "/careers?work=hybrid" },
  { key: "on-site", label: "On-site", href: "/careers?work=on-site" },
  { key: "flexible", label: "Flexible hours", href: "/careers?work=flexible" },
  { key: "shift", label: "Shift-based", href: "/careers?work=shift" },
  { key: "self-employed", label: "Self-employed", href: "/careers?filter=solo" },
];
const MEGA_INTENTS: { label: string; to: string }[] = [
  { label: "High-paying careers", to: "/careers/intent/high-paying" },
  { label: "No-degree careers", to: "/careers/intent/no-degree" },
  { label: "Creative careers", to: "/careers/intent/creative" },
  { label: "Stable careers", to: "/careers/intent/stable" },
  { label: "Career change paths", to: "/careers/intent/career-change" },
];

// Course mega menu data. Category slugs match public.course_categories.
const MEGA_COURSE_CATEGORIES: { slug: string; label: string }[] = [
  { slug: "ai-data-automation", label: "AI, Data and Automation" },
  { slug: "business-finance-legal", label: "Business and Finance" },
  { slug: "technology", label: "Technology" },
  { slug: "marketing-media", label: "Marketing and Media" },
  { slug: "design-creative", label: "Design and Creative" },
  { slug: "healthcare-care", label: "Healthcare" },
  { slug: "beauty-aesthetics", label: "Beauty and Aesthetics" },
  { slug: "education-science-research", label: "Education and Science" },
  { slug: "sport-fitness-wellbeing", label: "Sport, Fitness and Wellbeing" },
  { slug: "hospitality-food", label: "Hospitality and Food" },
  { slug: "mental-health-therapy", label: "Mental Health and Therapy" },
  { slug: "green-energy-sustainability", label: "Green Energy" },
  { slug: "sales-retail-customer-service", label: "Sales and Retail" },
  { slug: "self-employed", label: "Self-Employed" },
];
const MEGA_COURSE_GOALS: { label: string; to: string }[] = [
  { label: "Career change courses", to: "/courses?goal=career-change" },
  { label: "Beginner-friendly courses", to: "/courses?level=beginner" },
  { label: "Certificate courses", to: "/courses?certificate=true" },
  { label: "Free trial courses", to: "/courses?trial=free" },
  { label: "Online / self-paced courses", to: "/courses?format=online" },
];
const MEGA_COURSE_LEARN: { label: string; to: string }[] = [
  { label: "Beginner", to: "/courses?level=beginner" },
  { label: "Intermediate", to: "/courses?level=intermediate" },
  { label: "Advanced", to: "/courses?level=advanced" },
  { label: "Certificate available", to: "/courses?certificate=true" },
  { label: "CPD / accredited", to: "/courses?accredited=true" },
  { label: "Short courses", to: "/courses?duration=short" },
  { label: "Online courses", to: "/courses?format=online" },
];

const navItems: { label: string; path: string; external?: boolean }[] = [
  { label: "How It Works", path: "/how-it-works" },
  { label: "Careers", path: "/careers" },
  { label: "Courses", path: "/courses" },
  { label: "Pricing", path: "/pricing" },
];

const Header = ({ backAction }: { backAction?: { label?: string; onClick: () => void } } = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { status, userName } = useAssessmentState();
  const { user, signOut } = useAuth();
  const { matches, blueprint, accelerator } = useUnlocks();
  const { isActive: hasMembership } = useSubscription();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { count: savedCount } = useShortlist();
  const [mobileLegalOpen, setMobileLegalOpen] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const [signInMode, setSignInMode] = useState<"signin" | "signup">("signin");
  const openAuth = (mode: "signin" | "signup") => {
    setSignInMode(mode);
    setSignInOpen(true);
  };
  const [retakeOpen, setRetakeOpen] = useState(false);
  const [careersOpen, setCareersOpen] = useState(false);
  const [coursesOpen, setCoursesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [heartPulse, setHeartPulse] = useState(false);
  useEffect(() => {
    const onPulse = () => {
      setHeartPulse(false);
      requestAnimationFrame(() => setHeartPulse(true));
      window.setTimeout(() => setHeartPulse(false), 700);
    };
    window.addEventListener("tk:shortlist-pulse", onPulse);
    return () => window.removeEventListener("tk:shortlist-pulse", onPulse);
  }, []);
  const [profile, setProfile] = useState<{ first_name: string | null; last_name: string | null; display_name: string | null } | null>(null);

  useEffect(() => {
    if (!user) {
      setProfile(null);
      return;
    }
    let cancelled = false;
    supabase
      .from("profiles")
      .select("first_name, last_name, display_name")
      .eq("id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setProfile(data ?? null);
      });
    const handler = () => {
      supabase
        .from("profiles")
        .select("first_name, last_name, display_name")
        .eq("id", user.id)
        .maybeSingle()
        .then(({ data }) => {
          if (!cancelled) setProfile(data ?? null);
        });
    };
    window.addEventListener("assessmentStateChanged", handler);
    return () => {
      cancelled = true;
      window.removeEventListener("assessmentStateChanged", handler);
    };
  }, [user]);

  // Direction-aware bar sizing:
  //  - Scrolling DOWN  -> contracted (narrower) bar
  //  - Scrolling UP    -> expanded  (wider)    bar
  //  - At the very top -> always expanded
  // Listens to BOTH native scroll and Lenis "scroll" events so it works
  // regardless of whether smooth-scroll is active.
  useEffect(() => {
    let lastY = typeof window !== "undefined" ? window.scrollY : 0;
    let accum = 0;
    const THRESHOLD = 8;

    const apply = (y: number) => {
      const delta = y - lastY;
      if (y <= 8) {
        accum = 0;
        lastY = y;
        setScrolled(false);
        return;
      }
      // Accumulate same-direction movement; reset on direction change.
      if ((delta > 0 && accum < 0) || (delta < 0 && accum > 0)) accum = 0;
      accum += delta;
      lastY = y;
      if (accum > THRESHOLD) {
        setScrolled(true);
        accum = 0;
      } else if (accum < -THRESHOLD) {
        setScrolled(false);
        accum = 0;
      }
    };

    const onNativeScroll = () => apply(window.scrollY);
    const onLenisScroll = (e: Event) => {
      const detail = (e as CustomEvent).detail as { scroll?: number } | undefined;
      const y = typeof detail?.scroll === "number" ? detail.scroll : window.scrollY;
      apply(y);
    };

    window.addEventListener("scroll", onNativeScroll, { passive: true });
    window.addEventListener("lenis-scroll", onLenisScroll as EventListener);
    return () => {
      window.removeEventListener("scroll", onNativeScroll);
      window.removeEventListener("lenis-scroll", onLenisScroll as EventListener);
    };
  }, []);

  const hasAnyUnlock = matches || blueprint || accelerator;

  const goToMyPath = () => {
    navigate("/results");
  };

  const handleEditAnswers = () => {
    navigate("/assessment?edit=1");
  };

  const handleRetake = () => {
    setRetakeOpen(true);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const toTitleCase = (s: string) =>
    s
      .trim()
      .split(/\s+/)
      .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w))
      .join(" ");

  const profileFullName = [profile?.first_name, profile?.last_name].filter(Boolean).join(" ").trim();
  const rawName =
    profileFullName ||
    profile?.display_name ||
    userName ||
    user?.email?.split("@")[0] ||
    "Account";
  const displayName = toTitleCase(rawName);

  const initials = (() => {
    const first = profile?.first_name?.trim();
    const last = profile?.last_name?.trim();
    if (first && last) return (first[0] + last[0]).toUpperCase();
    const source = (profileFullName || profile?.display_name || userName || user?.email || "").trim();
    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return (source.slice(0, 2) || "??").toUpperCase();
  })();

  // State-aware primary CTA based on assessment + purchases
  const primaryCta = (() => {
    if (status !== "completed") {
      return {
        label: status === "in_progress" ? "Resume Assessment" : "Start Assessment",
        action: () => navigate("/assessment"),
        icon: null as JSX.Element | null,
      };
    }
    if (hasMembership) {
      return {
        label: "My Career Growth",
        action: () => navigate("/results"),
        icon: <TrendingUp className="h-4 w-4" />,
      };
    }
    if (blueprint) {
      return {
        label: "My Career Blueprint",
        action: () => navigate("/blueprint"),
        icon: <CompassIcon className="h-4 w-4" />,
      };
    }
    // Completed assessment but no purchase yet - send to results (preview of
    // careers 6-10 + locked top 5 + unlock CTA), not the unlocked
    // /career-matches page.
    return matches
      ? {
          label: "My Career Matches",
          action: () => navigate("/career-matches"),
          icon: <BarChart3 className="h-4 w-4" />,
        }
      : {
          label: "My Results",
          action: () => navigate("/results"),
          icon: <BarChart3 className="h-4 w-4" />,
        };
  })();

  const renderSplitButton = () => (
    <div className="inline-flex items-stretch rounded-full bg-tk-primary text-white shadow-sm overflow-hidden font-display h-9">
      <button
        type="button"
        onClick={primaryCta.action}
        className="pl-5 pr-4 text-sm font-medium hover:bg-tk-primary/90 transition-colors whitespace-nowrap focus:outline-none focus-visible:outline-none"
      >
        {primaryCta.label}
      </button>
      <div className="w-px bg-white/30" aria-hidden />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="px-2.5 flex items-center justify-center hover:bg-tk-primary/90 transition-colors text-white focus:outline-none focus-visible:outline-none"
            aria-label="More options"
          >
            <ChevronDown className="h-4 w-4" strokeWidth={2.5} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onClick={() => navigate(matches ? "/career-matches" : "/results")} className="cursor-pointer gap-2">
            <BarChart3 className="h-4 w-4" />
            {matches ? "My Career Matches" : "My Results"}
          </DropdownMenuItem>
          {blueprint && (
            <DropdownMenuItem onClick={() => navigate("/blueprint")} className="cursor-pointer gap-2">
              <CompassIcon className="h-4 w-4" />
              My Career Blueprint
            </DropdownMenuItem>
          )}
          {hasMembership && (
            <DropdownMenuItem onClick={() => navigate("/results")} className="cursor-pointer gap-2">
              <TrendingUp className="h-4 w-4" />
              My Career Growth
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate("/careers")} className="cursor-pointer gap-2">
            <List className="h-4 w-4" />
            View all careers
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  const renderCTA = () => {
    // Logged-in users: Account icon menu + state-aware CTA
    if (user) {
      return (
        <div className="flex items-center gap-2 whitespace-nowrap">
          <button
            type="button"
            onClick={() => navigate("/saved")}
            aria-label={`Saved careers (${savedCount})`}
            data-shortlist-target
            className={cn(
              "relative inline-flex items-center justify-center h-9 w-9 rounded-full text-tk-text/70 hover:text-tk-text hover:bg-tk-surface transition-colors",
              heartPulse && "text-tk-primary",
            )}
          >
            <Heart
              className={cn("h-4 w-4 transition-transform", heartPulse && "animate-heart-pop")}
              fill={savedCount > 0 ? "currentColor" : "none"}
            />
            {savedCount > 0 && (
              <span className={cn(
                "absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-tk-primary text-white text-[10px] font-semibold flex items-center justify-center",
                heartPulse && "animate-heart-pop",
              )}>
                {savedCount}
              </span>
            )}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-1.5 rounded-full pr-1 pl-1 py-1 hover:bg-tk-surface transition-colors"
                aria-label="Account menu"
              >
                <Avatar className="h-8 w-8 bg-tk-primary text-white">
                  <AvatarFallback className="bg-tk-primary text-white text-xs font-medium">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <ChevronDown className="h-3.5 w-3.5 text-tk-text/60" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium truncate">{displayName}</p>
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/account")} className="cursor-pointer gap-2">
                <UserCircle className="h-4 w-4" />
                Account
              </DropdownMenuItem>
              {status === "completed" && (
                <DropdownMenuItem onClick={handleEditAnswers} className="cursor-pointer gap-2">
                  <Pencil className="h-4 w-4" />
                  Edit my answers
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={handleRetake} className="cursor-pointer gap-2">
                <RotateCcw className="h-4 w-4" />
                Retake assessment
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/account#billing")} className="cursor-pointer gap-2">
                <CreditCard className="h-4 w-4" />
                Billing & purchases
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/account#settings")} className="cursor-pointer gap-2">
                <Settings className="h-4 w-4" />
                Account settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer gap-2 text-muted-foreground">
                <LogOut className="h-4 w-4" />
                Sign out
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/account#delete")} className="cursor-pointer gap-2 text-destructive focus:text-destructive">
                <Trash2 className="h-4 w-4" />
                Delete account
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {status === "completed" ? (
            renderSplitButton()
          ) : (
            <Button
              size="sm"
              className="rounded-full px-5 bg-tk-primary text-white hover:bg-tk-primary/90 font-display whitespace-nowrap"
              onClick={primaryCta.action}
            >
              {primaryCta.label}
            </Button>
          )}
        </div>
      );
    }

    // Not signed in
    return (
      <div className="flex items-center gap-2 whitespace-nowrap">
        <button
          type="button"
          onClick={() => navigate("/saved")}
          aria-label={`Saved careers (${savedCount})`}
          data-shortlist-target
          className={cn(
            "relative inline-flex items-center justify-center h-9 w-9 rounded-full text-tk-text/70 hover:text-tk-text hover:bg-tk-surface transition-colors",
            heartPulse && "text-tk-primary",
          )}
        >
          <Heart
            className={cn("h-4 w-4 transition-transform", heartPulse && "animate-heart-pop")}
            fill={savedCount > 0 ? "currentColor" : "none"}
          />
          {savedCount > 0 && (
            <span className={cn(
              "absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-tk-primary text-white text-[10px] font-semibold flex items-center justify-center",
              heartPulse && "animate-heart-pop",
            )}>
              {savedCount}
            </span>
          )}
        </button>
        <Button
          size="icon"
          variant="ghost"
          className="rounded-full text-tk-text/70 hover:text-tk-text hover:bg-tk-surface"
          onClick={() => setSignInOpen(true)}
          aria-label="Sign in"
        >
          <User className="h-4 w-4" />
        </Button>
        {status === "completed" ? (
          renderSplitButton()
        ) : (
          <Button
            size="sm"
            className="rounded-full px-5 bg-tk-primary text-white hover:bg-tk-primary/90 font-display whitespace-nowrap"
            onClick={primaryCta.action}
          >
            {primaryCta.label}
          </Button>
        )}
      </div>
    );
  };

  // Determine the primary CTA action for the mobile menu footer
  const mobilePrimaryCTA = () => {
    return { label: primaryCta.label, action: primaryCta.action };
  };

  return (
    <>
      <header
        className="sticky top-0 z-50 py-4 lg:py-5 font-display transition-[padding] duration-500 ease-out"
        style={{
          paddingLeft: scrolled ? "3.5rem" : "1rem",
          paddingRight: scrolled ? "3.5rem" : "1rem",
        }}
      >
        <div
          className="relative mx-auto w-full transition-[max-width] duration-500 ease-out"
          style={{
            maxWidth: scrolled ? "64rem" : "72rem",
          }}
        >
          <Popover open={careersOpen} onOpenChange={setCareersOpen}>
          <PopoverAnchor asChild>
          <div>
          <Popover open={coursesOpen} onOpenChange={setCoursesOpen}>
          <PopoverAnchor asChild>
          <div className="relative rounded-full bg-background border border-tk-text/5 text-tk-text shadow-[0_10px_40px_-15px_hsl(var(--tk-text)/0.18)] px-5 lg:px-7 py-[6px] lg:py-3">


            <div className="relative flex items-center justify-between gap-6">
              {/* Left: optional Back + Logo */}
              <div className="flex items-center gap-3 shrink-0">
                {backAction && (
                  <button
                    onClick={backAction.onClick}
                    className="inline-flex items-center gap-1 text-xs text-tk-text/60 hover:text-tk-text transition-colors"
                    aria-label="Back"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">{backAction.label ?? "Back"}</span>
                  </button>
                )}
                <button
                  onClick={() => navigate("/")}
                  className="flex items-center justify-start shrink-0"
                  aria-label="Tony Knows home"
                >
                  <img src={logo} alt="Tony Knows" width={144} height={28} decoding="async" className="h-[1.4rem] lg:h-7 w-auto shrink-0 max-w-none" />

                </button>
              </div>

              {/* Center: Nav - absolutely centered to the bar so width animation doesn't shift it */}
              <nav className="hidden lg:flex items-center gap-2 lg:gap-3 whitespace-nowrap">
                {navItems.map((item) => {
                  const onClick = () => {
                    if (item.external) {
                      window.open(item.path, "_blank", "noopener,noreferrer");
                    } else if (item.path === "/my-path") {
                      goToMyPath();
                    } else {
                      navigate(item.path);
                    }
                  };
                  const showDot = item.path === "/my-path" && hasAnyUnlock;
                  const isActive = !item.external && (
                    item.path === "/"
                      ? location.pathname === "/"
                      : location.pathname === item.path || location.pathname.startsWith(item.path + "/")
                  );
                  const baseClass = `relative inline-flex items-center whitespace-nowrap text-sm font-medium cursor-pointer rounded-full px-3 py-1.5 md:px-3.5 md:py-1.5 text-tk-text transition-all duration-200 ease-out ${
                    isActive
                      ? "bg-tk-text/[0.06] shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:bg-tk-text/[0.09]"
                      : "hover:bg-tk-text/[0.04] hover:scale-[1.02]"
                  }`;

                  if (item.label === "Careers" || item.label === "Courses") {
                    const isCourses = item.label === "Courses";
                    const open = isCourses ? coursesOpen : careersOpen;
                    const setOpen = isCourses ? setCoursesOpen : setCareersOpen;
                    const closeOther = isCourses ? setCareersOpen : setCoursesOpen;
                    const activeOrOpen = isActive || open;
                    const pillCls = `relative inline-flex items-center whitespace-nowrap text-sm font-medium cursor-pointer rounded-full px-3 py-1.5 md:px-3.5 md:py-1.5 transition-all duration-200 ease-out gap-1 ${
                      activeOrOpen
                        ? "bg-tk-primary/10 text-tk-primary"
                        : "text-tk-text hover:bg-tk-text/[0.04] hover:scale-[1.02]"
                    }`;
                    return (
                      <button
                        key={item.label}
                        type="button"
                        className={pillCls}
                        aria-current={isActive ? "page" : undefined}
                        aria-haspopup="true"
                        aria-expanded={open}
                        onClick={() => { closeOther(false); setOpen(!open); }}
                      >
                        {item.label}
                        <ChevronDown
                          className={`h-3.5 w-3.5 transition-transform duration-200 ${
                            open ? "rotate-180 text-tk-primary" : "text-tk-text/60"
                          }`}
                        />
                      </button>
                    );
                  }

                  return (
                    <button
                      key={item.label}
                      type="button"
                      className={baseClass}
                      onClick={onClick}
                      aria-current={isActive ? "page" : undefined}
                    >
                      {item.label}
                      {showDot && (
                        <span
                          className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-tk-accent"
                          aria-hidden="true"
                        />
                      )}
                      {showDot && <span className="sr-only">New unlock</span>}
                    </button>
                  );
                })}
              </nav>

              {/* Right: CTA */}
              <div className="flex items-center gap-2 justify-end shrink-0">
                <div className="hidden lg:flex items-center gap-2">
                  {renderCTA()}
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => setMobileOpen((v) => !v)}
                  className="lg:hidden h-11 w-11 md:h-9 md:w-9 bg-transparent hover:bg-transparent focus-visible:bg-transparent active:bg-transparent text-tk-text hover:text-tk-text focus-visible:text-tk-text active:text-tk-text"
                  aria-label={mobileOpen ? "Close menu" : "Open menu"}
                >
                  {mobileOpen
                    ? <X className="!h-7 !w-7 md:!h-5 md:!w-5 text-tk-text" strokeWidth={2.25} />
                    : <Menu className="!h-7 !w-7 md:!h-5 md:!w-5 text-tk-text" strokeWidth={2.25} />}
                </Button>
              </div>
            </div>
          </div>
          </PopoverAnchor>
          <PopoverContent
            align="center"
            sideOffset={8}
            collisionPadding={16}
            className="w-[min(1140px,calc(100vw-2rem))] p-7 rounded-2xl border border-tk-border bg-white shadow-[0_18px_50px_-20px_hsl(var(--tk-text)/0.18)]"
          >
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-3">
                <Link
                  to="/courses"
                  onClick={() => setCoursesOpen(false)}
                  className="group flex items-center justify-between w-full rounded-lg px-3 py-2.5 bg-tk-text text-white text-sm font-semibold hover:bg-tk-text/90 transition-colors mb-4"
                >
                  <span>Browse all courses</span>
                  <ChevronRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
                <div className="px-2 mb-3.5 text-[12px] uppercase tracking-[0.12em] text-tk-primary font-bold">
                  Explore by goal
                </div>
                <div className="flex flex-col">
                  {MEGA_COURSE_GOALS.map((g) => (
                    <Link
                      key={g.to}
                      to={g.to}
                      onClick={() => setCoursesOpen(false)}
                      className="block rounded-md px-2 py-1.5 text-sm text-tk-text/80 hover:text-tk-text hover:bg-tk-surface transition-colors"
                    >
                      {g.label}
                    </Link>
                  ))}
                </div>
              </div>
              <div className="col-span-6">
                <div className="px-2 mb-3.5 text-[12px] uppercase tracking-[0.12em] text-tk-primary font-bold">
                  Course categories
                </div>
                <div className="grid grid-cols-2 gap-x-2">
                  {MEGA_COURSE_CATEGORIES.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/courses?category=${c.slug}`}
                      onClick={() => setCoursesOpen(false)}
                      title={c.label}
                      className="block rounded-md px-2 py-1.5 text-sm text-tk-text/80 hover:text-tk-text hover:bg-tk-surface transition-colors truncate"
                    >
                      {c.label}
                    </Link>
                  ))}
                </div>
              </div>
              <div className="col-span-3">
                <div className="px-2 mb-3.5 text-[12px] uppercase tracking-[0.12em] text-tk-primary font-bold">
                  Ways to learn
                </div>
                <div className="flex flex-col">
                  {MEGA_COURSE_LEARN.map((l) => (
                    <Link
                      key={l.label}
                      to={l.to}
                      onClick={() => setCoursesOpen(false)}
                      className="block rounded-md px-2 py-1.5 text-sm text-tk-text/80 hover:text-tk-text hover:bg-tk-surface transition-colors"
                    >
                      {l.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </PopoverContent>
          </Popover>
          </div>
          </PopoverAnchor>
          <PopoverContent
            align="center"
            sideOffset={8}
            collisionPadding={16}
            className="w-[min(1140px,calc(100vw-2rem))] p-7 rounded-2xl border border-tk-border bg-white shadow-[0_18px_50px_-20px_hsl(var(--tk-text)/0.18)]"
          >
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-3">
                <Link
                  to="/careers"
                  onClick={() => setCareersOpen(false)}
                  className="group flex items-center justify-between w-full rounded-lg px-3 py-2.5 bg-tk-text text-white text-sm font-semibold hover:bg-tk-text/90 transition-colors mb-4"
                >
                  <span>Browse all careers</span>
                  <ChevronRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
                <div className="px-2 mb-3.5 text-[12px] uppercase tracking-[0.12em] text-tk-primary font-bold">
                  Explore by goal
                </div>
                <div className="flex flex-col">
                  {MEGA_INTENTS.map((i) => (
                    <Link
                      key={i.to}
                      to={i.to}
                      onClick={() => setCareersOpen(false)}
                      className="block rounded-md px-2 py-1.5 text-sm text-tk-text/80 hover:text-tk-text hover:bg-tk-surface transition-colors"
                    >
                      {i.label}
                    </Link>
                  ))}
                </div>
              </div>
              <div className="col-span-6">
                <div className="px-2 mb-3.5 text-[12px] uppercase tracking-[0.12em] text-tk-primary font-bold">
                  Career categories
                </div>
                <div className="grid grid-cols-2 gap-x-2">
                  {MEGA_CATEGORIES.map((c) => (
                    <Link
                      key={c}
                      to={`/careers?cat=${encodeURIComponent(c)}`}
                      onClick={() => setCareersOpen(false)}
                      title={categoryLabel(c)}
                      className="block rounded-md px-2 py-1.5 text-sm text-tk-text/80 hover:text-tk-text hover:bg-tk-surface transition-colors truncate"
                    >
                      {categoryLabel(c)}
                    </Link>
                  ))}
                </div>
              </div>
              <div className="col-span-3 flex flex-col gap-5">
                <div>
                  <div className="px-2 mb-3.5 text-[12px] uppercase tracking-[0.12em] text-tk-primary font-bold">
                    Ways to start
                  </div>
                  <div className="flex flex-col">
                    {MEGA_BARRIERS.map((b) => (
                      <Link
                        key={b}
                        to={`/careers?barrier=${encodeURIComponent(b)}`}
                        onClick={() => setCareersOpen(false)}
                        className="block rounded-md px-2 py-1.5 text-sm text-tk-text/80 hover:text-tk-text hover:bg-tk-surface transition-colors"
                      >
                        {ENTRY_BARRIER_LABELS[b]}
                      </Link>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="px-2 mb-3.5 text-[12px] uppercase tracking-[0.12em] text-tk-primary font-bold">
                    Work styles
                  </div>
                  <div className="flex flex-col">
                    {MEGA_WORKSTYLES.map((w) => (
                      <Link
                        key={w.key}
                        to={w.href}
                        onClick={() => setCareersOpen(false)}
                        className="block rounded-md px-2 py-1.5 text-sm text-tk-text/80 hover:text-tk-text hover:bg-tk-surface transition-colors"
                      >
                        {w.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </PopoverContent>
          </Popover>

        </div>
      </header>

      {/* Mobile right-side sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent
          side="right"
          className="lg:hidden w-[82%] max-w-sm bg-white border-l border-tk-text/5 p-0 flex flex-col"
        >
          <div className="flex items-center justify-between px-6 pt-6 pb-4">
            <img src={logo} alt="Tony Knows" width={144} height={28} decoding="async" className="h-7 w-auto" />
          </div>

          <nav className="flex-1 flex flex-col px-4 py-2 overflow-y-auto">
            {[
              { label: "How It Works", path: "/how-it-works" },
              { label: "Careers", path: "/careers" },
              { label: "Courses", path: "/courses" },
              { label: `Saved careers${savedCount > 0 ? ` (${savedCount})` : ""}`, path: "/saved" },
              { label: "Pricing", path: "/pricing" },
              { label: "FAQs", path: "/faq" },
              { label: "Contact us", path: "/contact" },
            ].map((item) => {
              const onClick = () => {
                setMobileOpen(false);
                navigate(item.path);
              };
              return (
                <button
                  key={item.label}
                  onClick={onClick}
                  className="w-full px-3 py-1 text-left text-base font-medium text-tk-text/80 hover:text-tk-text hover:bg-tk-surface/60 rounded-xl transition-colors"
                >
                  {item.label}
                </button>
              );
            })}

            {/* Expandable: legal / more */}
            <button
              type="button"
              onClick={() => setMobileLegalOpen((v) => !v)}
              aria-expanded={mobileLegalOpen}
              className="w-full px-3 py-1 text-left text-base font-medium text-tk-text/80 hover:text-tk-text hover:bg-tk-surface/60 rounded-xl transition-colors flex items-center justify-between"
            >
              <span>More</span>
              <ChevronRight
                className={`h-4 w-4 text-tk-text/50 transition-transform ${mobileLegalOpen ? "rotate-90" : ""}`}
              />
            </button>
            {mobileLegalOpen && (
              <div className="pl-3 mt-0.5 mb-1 flex flex-col">
                {[
                  { label: "Terms", path: "/terms" },
                  { label: "Privacy", path: "/privacy" },
                ].map((item) => (
                  <button
                    key={item.label}
                    onClick={() => {
                      setMobileOpen(false);
                      navigate(item.path);
                    }}
                    className="w-full px-3 py-1 text-left text-sm font-medium text-tk-text/70 hover:text-tk-text hover:bg-tk-surface/60 rounded-xl transition-colors"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}

          </nav>

          {user ? (
            <div className="px-4 pt-3 pb-2 border-t border-tk-text/5">
              <div className="flex items-center gap-3 px-3 pb-3">
                <Avatar className="h-9 w-9 bg-tk-primary text-white">
                  <AvatarFallback className="bg-tk-primary text-white text-xs font-medium">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate text-tk-text">{displayName}</p>
                  <p className="text-xs text-tk-text/60 truncate">{user.email}</p>
                </div>
              </div>
              <div className="flex flex-col">
                {(status === "completed"
                  ? [
                      { label: "Account", icon: UserCircle, onClick: () => navigate("/account") },
                      { label: "Edit my answers", icon: Pencil, onClick: handleEditAnswers },
                      { label: "Retake assessment", icon: RotateCcw, onClick: handleRetake },
                      { label: "Billing & purchases", icon: CreditCard, onClick: () => navigate("/account#billing") },
                      { label: "Account settings", icon: Settings, onClick: () => navigate("/account#settings") },
                      { label: "Sign out", icon: LogOut, onClick: handleSignOut },
                    ]
                  : [
                      { label: status === "in_progress" ? "Resume assessment" : "Take assessment", icon: ClipboardList, onClick: () => navigate("/assessment") },
                      { label: "Account settings", icon: Settings, onClick: () => navigate("/account#settings") },
                      { label: "Sign out", icon: LogOut, onClick: handleSignOut },
                    ]
                ).map(({ label, icon: Icon, onClick }) => (
                  <button
                    key={label}
                    onClick={() => {
                      setMobileOpen(false);
                      onClick();
                    }}
                    className="w-full px-3 py-2 text-left text-sm font-medium text-tk-text/80 hover:text-tk-text hover:bg-tk-surface/60 rounded-xl transition-colors flex items-center gap-3"
                  >
                    <Icon className="h-4 w-4 text-tk-text/60" />
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="px-4 pt-3 pb-2 border-t border-tk-text/5">
              <div className="flex flex-col">
                {[
                  { label: "Create account", icon: UserPlus, onClick: () => openAuth("signup") },
                  { label: "Take assessment", icon: ClipboardList, onClick: () => navigate("/assessment") },
                  { label: "Sign in", icon: LogIn, onClick: () => openAuth("signin") },
                ].map(({ label, icon: Icon, onClick }) => (
                  <button
                    key={label}
                    onClick={() => {
                      setMobileOpen(false);
                      onClick();
                    }}
                    className="w-full px-3 py-2 text-left text-sm font-medium text-tk-text/80 hover:text-tk-text hover:bg-tk-surface/60 rounded-xl transition-colors flex items-center gap-3"
                  >
                    <Icon className="h-4 w-4 text-tk-text/60" />
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="px-6 pb-8 pt-4 border-t border-tk-text/5">
            <Button
              size="lg"
              className="w-full rounded-full bg-tk-primary text-white hover:bg-tk-primary/90"
              onClick={() => {
                setMobileOpen(false);
                mobilePrimaryCTA().action();
              }}
            >
              {mobilePrimaryCTA().label}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
      <SignInModal open={signInOpen} onOpenChange={setSignInOpen} initialMode={signInMode} />
      <RetakeAssessmentDialog open={retakeOpen} onOpenChange={setRetakeOpen} />
    </>
  );
};

export default Header;
